import { useMemo, useRef, useState } from "react"
import { ChevronDown } from "lucide-react"
import { Field } from "./fields"

let idSeq = 0

const normalize = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()

// Type-to-search replacement for a long native <select>, whose OS popup
// renders clipped on small screens. Same props as Select; the list always
// opens below the field, inside the page.
const SearchSelect = ({ label, required, hint, error, value, onChange, options = [], placeholder = "Select…", disabled }) => {
  const [fid] = useState(() => `pc-ss-${(idSeq += 1)}`)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(null)
  const [active, setActive] = useState(0)
  const listRef = useRef(null)

  const selected = options.find((o) => String(o.value) === String(value ?? ""))
  const matches = useMemo(() => {
    const q = normalize(query)
    return q ? options.filter((o) => normalize(o.label).includes(q)) : options
  }, [options, query])

  const openList = () => {
    if (disabled) return
    setOpen(true)
    setActive(Math.max(0, matches.indexOf(selected)))
  }
  const close = () => {
    setOpen(false)
    setQuery(null)
  }
  const pick = (o) => {
    onChange?.(o.value)
    close()
  }

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      if (!open) {
        openList()
        return
      }
      const next = Math.max(0, Math.min(matches.length - 1, active + (e.key === "ArrowDown" ? 1 : -1)))
      setActive(next)
      listRef.current?.children[next]?.scrollIntoView({ block: "nearest" })
    } else if (e.key === "Enter" && open) {
      e.preventDefault()
      if (matches[active]) pick(matches[active])
    } else if (e.key === "Escape") {
      close()
    }
  }

  return (
    <Field label={label} required={required} hint={hint} error={error} htmlFor={fid}>
      <div style={{ position: "relative" }}>
        <input
          id={fid}
          className="pc-control"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${fid}-list`}
          aria-autocomplete="list"
          aria-invalid={!!error || undefined}
          autoComplete="off"
          placeholder={placeholder}
          value={query ?? selected?.label ?? ""}
          disabled={disabled}
          onFocus={openList}
          onClick={openList}
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
            if (!open) setOpen(true)
          }}
          onBlur={close}
          onKeyDown={onKeyDown}
          style={{ paddingRight: 32, textOverflow: "ellipsis" }}
        />
        <ChevronDown
          aria-hidden
          size={16}
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "var(--pc-muted)", pointerEvents: "none" }}
        />
        {open && (
          <ul
            id={`${fid}-list`}
            ref={listRef}
            role="listbox"
            style={{
              position: "absolute",
              zIndex: 20,
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              maxHeight: 260,
              overflowY: "auto",
              margin: 0,
              padding: 4,
              listStyle: "none",
              background: "var(--pc-surface)",
              border: "1px solid var(--pc-border)",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(15,23,42,.12)",
            }}
          >
            {matches.length === 0 ? (
              <li style={{ padding: "8px 10px", color: "var(--pc-muted)", fontSize: 13 }}>No match.</li>
            ) : (
              matches.map((o, i) => (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={o === selected}
                  // mousedown, not click: fires before the input's blur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(o)
                  }}
                  onMouseEnter={() => setActive(i)}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 13.5,
                    background: i === active ? "var(--pc-surface-2)" : undefined,
                    fontWeight: o === selected ? 600 : 400,
                    color: "var(--pc-ink)",
                  }}
                >
                  {o.label}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </Field>
  )
}

export default SearchSelect
